import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BadgeCheckIcon, EyeOffIcon, MessageSquareIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { EmptyState } from '../../components/ui/EmptyState';
import { ReportControls } from '../../components/trust/ReportControls';
import { AcademicLine, StandingChip } from '../../components/verification/StandingChip';
import { groups, personByHandle } from '../../data/community';
import { providerByHandle } from '../../data/marketplace';
import { standingFor } from '../../data/standings';
import type { VisibilityTier } from '../../types/community';
const TIER_LABEL: Record<VisibilityTier, string> = {
  public: 'Public',
  campus: 'Campus only',
  friends: 'Friends only'
};
export function PersonProfile() {
  const {
    handle = ''
  } = useParams();
  const navigate = useNavigate();
  const person = personByHandle(handle);
  if (!person) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-11139" data-mp="3nhtrjq82awhw">
        <EmptyState icon={<UsersIcon className="h-4 w-4" data-id="element-11141" data-mp="39m7v4xzi1afg" />} title="Profile not found" description="That handle does not exist, or the person has restricted who can find them." action={<Button variant="secondary" onClick={() => navigate('/community')} data-id="element-11142" data-mp="2zs8vc5mwe2qo">
              Back to community
            </Button>} data-id="element-11140" data-mp="39jd9x48nvlkz" />
      </div>;
  }
  const provider = person.providerHandle ? providerByHandle(person.providerHandle) : undefined;
  const isFriend = person.relationship === 'friend';
  const sharedGroups = groups.filter(group => person.sharedGroupIds.includes(group.id));
  const standing = standingFor(person.username);
  const fields: {
    label: string;
    value: string;
    tier: VisibilityTier;
  }[] = [{
    label: 'Programme',
    value: standing?.programme ?? person.programme,
    tier: person.visibility.programme
  }, {
    label: 'Housing area',
    value: person.housingArea,
    tier: person.visibility.housingArea
  }, {
    label: 'Career',
    value: person.careerHeadline,
    tier: person.visibility.careerProfile
  }, {
    label: 'Contact',
    value: `${person.username}@campuzon.ng`,
    tier: person.visibility.contact
  }];
  function canSee(tier: VisibilityTier) {
    if (tier === 'public') return true;
    if (tier === 'campus') return true;
    return isFriend;
  }
  return <div className="space-y-6" data-id="element-11143" data-mp="11ci6x4uueoyt">
      <Link to="/community" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-11144" data-mp="11bql2lr1s5lq">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-11145" data-mp="3m3ac9ooyib6w" />
        Community
      </Link>

      <div className="rounded-card border border-line bg-surface px-5 py-5" data-id="element-11146" data-mp="3h3xnuzifsw27">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-11147" data-mp="3h5qtqcl87c95">
          <div className="flex min-w-0 gap-4" data-id="element-11148" data-mp="1wn6nb462lt5g">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-50 text-lg font-semibold text-brand-800" data-id="element-11149" data-mp="21kgjd2nyt329">
              {person.initials}
            </span>
            <div className="min-w-0" data-id="element-11150" data-mp="2acry3r9pv22e">
              <div className="flex flex-wrap items-center gap-2" data-id="element-11151" data-mp="2adtmexfqtl22">
                <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-11152" data-mp="2fcmo7slo3nme">
                  {person.displayName}
                </h1>
                {person.identityVerified && <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-11154" data-mp="2pa149r3oiae6" />} data-id="element-11153" data-mp="2paat3bczmbia">
                    Identity verified
                  </Badge>}
                {/* Identity and academic standing are separate claims, so they
                    sit as separate badges rather than one ambiguous tick. */}
                <StandingChip standing={standing} data-id="element-11155" data-mp="yx3bx24ae72n" />
              </div>
              <p className="cz-num mt-1 text-[14px] text-ink-muted" data-id="element-11156" data-mp="3nokpul7zkk6">@{person.username}</p>
              <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-11157" data-mp="3pj597skxanq">{person.headline}</p>
              <div className="mt-2" data-id="element-11158" data-mp="3vtk3k8qx24dh">
                <AcademicLine standing={standing} fallback={`${person.university} · ${person.campus}`} data-id="element-11159" data-mp="dme6hyg8lp5p" />
              </div>
              <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-subtle" data-id="element-11160" data-mp="8kygdrz7csgg">
                <span data-id="element-11161" data-mp="3c3ruq6zt1lbo">{person.mutualFriends} mutual friends</span>
                <span data-id="element-11162" data-mp="3c1f475fze2n0">{person.lastActive}</span>
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2" data-id="element-11163" data-mp="3quh7hdswz8hn">
            <Button size="sm" variant={isFriend ? 'secondary' : 'primary'} icon={isFriend ? undefined : <UserPlusIcon className="h-3.5 w-3.5" data-id="element-11165" data-mp="2ya8z8ttxncsq" />} data-id="element-11164" data-mp="3m0mqwj8lv9ms">
              {isFriend ? 'Friends' : person.relationship === 'requested' ? 'Request sent' : 'Add friend'}
            </Button>
            <Button size="sm" variant="secondary" icon={<MessageSquareIcon className="h-3.5 w-3.5" data-id="element-11167" data-mp="1dlenubjbm7mt" />} data-id="element-11166" data-mp="18tn3iiqrcy54">
              Message
            </Button>
            <Button size="sm" variant="ghost" data-id="element-11168" data-mp="zutcr903z1zh">
              {person.following ? 'Following' : 'Follow'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11169" data-mp="13tsao9nf9yhf">
        <div className="space-y-6" data-id="element-11170" data-mp="1rlz4p25odtdo">
          <Panel title="Profile" description="Each field is shown according to the visibility this person chose." bodyClassName="p-0" data-id="element-11171" data-mp="1rjmmcnw09h03">
            <dl className="divide-y divide-line" data-id="element-11172" data-mp="1imvlly6iddz6">
              {fields.map(field => {
              const visible = canSee(field.tier);
              return <div key={field.label} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-11173" data-mp="74t4r1ab98se">
                    <dt className="w-28 shrink-0 text-[13px] text-ink-muted" data-id="element-11174" data-mp="c5gs9czbz7hv">{field.label}</dt>
                    <dd className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-11175" data-mp="2dsjp8w6gba2b">
                      {visible ? field.value : <span className="inline-flex items-center gap-1.5 text-ink-subtle" data-id="element-11176" data-mp="243jlkzh07qhs">
                          <EyeOffIcon className="h-3.5 w-3.5" data-id="element-11177" data-mp="202zsc6zjx70x" />
                          Shared with friends only
                        </span>}
                    </dd>
                    <Badge tone="neutral" data-id="element-11178" data-mp="1q4jy3v589hn7">{TIER_LABEL[field.tier]}</Badge>
                  </div>;
            })}
            </dl>
            <div className="border-t border-line px-5 py-3" data-id="element-11179" data-mp="1l5pj74adsd8d">
              <p className="text-[12px] leading-5 text-ink-muted" data-id="element-11180" data-mp="1h43y4vnp0sn3">
                Academic records, housing details, wellbeing information and financial identifiers are
                never exposed on a community profile, whatever the visibility setting.
              </p>
            </div>
          </Panel>

          <Panel title="Shared groups" bodyClassName="p-0" data-id="element-11181" data-mp="xanhqeg2c82z">
            {sharedGroups.length === 0 ? <p className="px-5 py-4 text-[13px] text-ink-muted" data-id="element-11182" data-mp="127x4fmc2i5n4">No groups in common.</p> : <ul className="divide-y divide-line" data-id="element-11183" data-mp="170yn85feapbz">
                {sharedGroups.map(group => <li key={group.id} className="flex items-center gap-3 px-5 py-3" data-id="element-11184" data-mp="1azz3phaehfvt">
                    <div className="min-w-0 flex-1" data-id="element-11185" data-mp="ebrmczsgxnnk">
                      <p className="text-[13px] font-medium text-ink" data-id="element-11186" data-mp="edcmd4xjxyst">{group.name}</p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-11187" data-mp="jc70vuohl174">
                        {group.members.toLocaleString()} members · {group.kind}
                      </p>
                    </div>
                    <Badge data-id="element-11188" data-mp="s8enlergezza">{group.privacy}</Badge>
                  </li>)}
              </ul>}
          </Panel>
        </div>

        <div className="space-y-6" data-id="element-11189" data-mp="23owlzkem3cok">
          <Panel title="Reputation" data-id="element-11190" data-mp="23s0x71qhv8oq">
            <ul className="space-y-3" data-id="element-11191" data-mp="1fcuky4bi61yj">
              {person.reputation.map(record => <li key={record.dimension} data-id="element-11192" data-mp="1aejcny0uvzif">
                  <Meter label={record.label} value={record.score} trailing={`${record.score}/100`} tone={record.score > 85 ? 'ok' : 'brand'} data-id="element-11193" data-mp="1afuehsaqqx6w" />
                </li>)}
            </ul>
            <p className="mt-3 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-11194" data-mp="1y2kd0jnvs9mr">
              Reputation is per domain. Strong standing as a tutor says nothing about their record as a
              tenant, and the two are never merged into one score.
            </p>
          </Panel>

          {provider && <Panel title="Sells on the marketplace" data-id="element-11195" data-mp="230u8kvdipc8w">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-11196" data-mp="crkax8vb41do">{provider.headline}</p>
              <p className="cz-num mt-1.5 text-[12px] text-ink-subtle" data-id="element-11197" data-mp="8ppb68t0xw20">
                {provider.rating.toFixed(1)} rating · {provider.completedOrders} completed orders ·{' '}
                {provider.packages.length} packages
              </p>
              <Link to="/marketplace" className="mt-3 inline-block" data-id="element-11198" data-mp="2lswqv75i4q5">
                <Button size="sm" variant="secondary" data-id="element-11199" data-mp="3kzsavfg9a069">
                  View services
                </Button>
              </Link>
            </Panel>}

          <Panel title="Safety" data-id="element-11200" data-mp="3b2nkf3fdnijn">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-11201" data-mp="3b1cm3j4pb2zx">
              Blocking removes this person from search, groups threads you share and any ability to
              message or request money from you. Reports go to the Campuzon trust queue and are never
              shared with them.
            </p>
            <div className="mt-3" data-id="element-11202" data-mp="371c3dmakk5n2">
              <ReportControls subject={person.displayName} surface="profile" allowBlock size="md" data-id="element-11203" data-mp="10acnyvk5qvlw" />
            </div>
          </Panel>
        </div>
      </div>
    </div>;
}